import React, { useEffect, useState, type ReactNode } from "react";

import {
  Tooltip as PrimitiveTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/primitives/Tooltip";

type TooltipPosition =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "top-start"
  | "top-end"
  | "bottom-start"
  | "bottom-end"
  | "left-start"
  | "left-end"
  | "right-start"
  | "right-end"
  | "auto";

/** Radix Tooltip.Content props we forward via ...otherProps. */
type TooltipContentProps = React.ComponentProps<typeof TooltipContent>;

export interface TooltipProps extends Omit<
  TooltipContentProps,
  "children" | "className" | "side" | "align" | "content"
> {
  /** Content rendered inside the tooltip popup. */
  content?: ReactNode;
  /** The trigger element. */
  children?: ReactNode;
  /** Disable the tooltip. */
  disabled?: boolean;
  /** Placement of the tooltip relative to the trigger. */
  position?: TooltipPosition;
  /** Whether the tooltip stays open when hovered over. */
  interactive?: boolean;
  /** Auto-hide after N milliseconds. Use -1 to disable. */
  hideAfter?: number;
  /** Additional CSS class names for the tooltip content. */
  className?: string;
  /** Delay in ms before the tooltip opens (forwarded to TooltipProvider). */
  delayDuration?: number;
  /** Radix Tooltip `defaultOpen` prop. */
  defaultOpen?: boolean;
}

import { SIDE_MAP, ALIGN_MAP } from "./constants";

const Tooltip = ({
  content,
  children,
  disabled = false,
  position = "auto",
  interactive = false,
  hideAfter = -1,
  className,
  delayDuration = 0,
  defaultOpen,
  // Radix TooltipContent props forwarded
  ...contentProps
}: TooltipProps) => {
  const [open, setOpen] = useState(false);

  // Auto-hide after hideAfter ms
  useEffect(() => {
    if (!open || hideAfter <= 0) return;
    const timer = setTimeout(() => setOpen(false), hideAfter);
    return () => clearTimeout(timer);
  }, [open, hideAfter]);

  if (disabled || !content) {
    return <>{children}</>;
  }

  const side = SIDE_MAP[position] ?? "top";
  const align = ALIGN_MAP[position] ?? "center";

  // Wrap non-element children in a span so TooltipTrigger can attach
  const trigger = React.isValidElement(children) ? (
    children
  ) : (
    <span>{children}</span>
  );

  return (
    <TooltipProvider delayDuration={delayDuration}>
      <PrimitiveTooltip
        open={open}
        onOpenChange={setOpen}
        defaultOpen={defaultOpen}
        disableHoverableContent={!interactive}
      >
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent
          side={side}
          align={align}
          className={className}
          onPointerDownOutside={
            interactive ? e => e.preventDefault() : undefined
          }
          {...contentProps}
        >
          {content}
        </TooltipContent>
      </PrimitiveTooltip>
    </TooltipProvider>
  );
};

Tooltip.displayName = "Tooltip";

export { Tooltip };
